// Planera jakt (J1): datamodellen för planeraren som rena funktioner —
// utkast, validering per steg, lottning av pass, mottagare och det som skickas till API:t.
import type { CalendarEventInvite, MarkCalendarEvent, PlanHuntInput } from '../../api';
// Deltagare, pass, lottning och markens såter delas med Q:s verktyg i API:t (QA2).
import { parsePlanSatar, peopleFromInvites, recipientsOf, passLabelFor, templateSatar, type Guest, type MarkerType, type PlanMember, type PlanSat } from '../../../../api/src/lib/huntPlan';
export {
  drawPosts, fairRandom, markSatNames, passLabelFor, postsOf, pruneAssignments, recipientsOf, toggleMarkSat, uid,
} from '../../../../api/src/lib/huntPlan';
export type {
  DrawResult, Guest, MarkPassPin, MarkSatChoice, MarkerType, PlanMarker, PlanMember, PlanSat, Post, Recipient,
} from '../../../../api/src/lib/huntPlan';

export const MARKER_LABEL: Record<MarkerType, string> = {
  pass: 'Pass', hund_start: 'Hundförare', samlingsplats: 'Samlingsplats', lunch: 'Lunch', fika: 'Fika', viltparad: 'Viltparad',
};

export interface PlanDraft {
  version: 2;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:MM */
  meetingTime: string;
  startTime: string;
  endTime: string;
  meetingPlace: string;
  meetingLat: number | null;
  meetingLng: number | null;
  satar: PlanSat[];
  memberIds: string[];
  guests: Guest[];
  message: string;
  utrustning: string[];
  contactName: string;
  contactPhone: string;
  sendEmail: boolean;
}

export const UTRUSTNING = [
  'Signalfärgad väst', 'Signalfärgad mössa', 'Jaktradio', 'Kompass', 'GPS', 'Kikare', 'Avståndsmätare',
  'Skjutstöd', 'Skjutstol', 'Förstahjälpen', 'Kniv', 'Draglina', 'Slaktväska', 'Termos', 'Egen mat', 'Hundväska',
];

export const STEPS = [
  { key: 'nar', label: 'När' },
  { key: 'plats', label: 'Plats' },
  { key: 'vilka', label: 'Vilka' },
  { key: 'pass', label: 'Pass' },
  { key: 'skicka', label: 'Skicka' },
] as const;
export type StepKey = typeof STEPS[number]['key'];

const pad = (n: number) => String(n).padStart(2, '0');
export const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const hhmm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Nästa lördag (eller idag om det är lördag). */
export function nextSaturday(now = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
  return isoDate(d);
}

export function emptyDraft(opts: { markName: string; date?: string; me?: PlanMember | null; userName?: string | null }): PlanDraft {
  return {
    version: 2,
    title: `Jakt – ${opts.markName}`,
    date: opts.date || nextSaturday(),
    meetingTime: '06:30',
    startTime: '07:30',
    endTime: '15:00',
    meetingPlace: '',
    meetingLat: null,
    meetingLng: null,
    satar: [],
    memberIds: opts.me ? [opts.me.id] : [],
    guests: [],
    message: '',
    utrustning: ['Signalfärgad väst', 'Signalfärgad mössa', 'Jaktradio'],
    contactName: opts.me?.name || opts.userName || '',
    contactPhone: opts.me?.phone || '',
    sendEmail: true,
  };
}

/** Lokal tid (YYYY-MM-DD + HH:MM) → ISO i UTC. Tom tid ger null. */
export function toIso(date: string, time: string): string | null {
  if (!date || !/^\d{1,2}:\d{2}$/.test(time || '')) return null;
  const d = new Date(`${date}T${time.padStart(5, '0')}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// ─── Validering per steg ──────────────────────────────────────────

export type Errors = Partial<Record<'title' | 'date' | 'meetingTime' | 'startTime' | 'endTime' | 'meetingPlace' | 'recipients', string>>;

/** meId = den planerandes medlems-id; jaktledaren är alltid med och räknas inte som mottagare. */
export function validateStep(step: StepKey, d: PlanDraft, meId?: string | null): Errors {
  const e: Errors = {};
  if (step === 'nar') {
    if (!d.title.trim()) e.title = 'Ge jakten en rubrik';
    if (!d.date) e.date = 'Välj datum';
    if (!toIso(d.date, d.meetingTime)) e.meetingTime = 'Ange samlingstid';
    if (!toIso(d.date, d.startTime)) e.startTime = 'Ange när jakten börjar';
    if (d.endTime && !toIso(d.date, d.endTime)) e.endTime = 'Skriv tiden som 15:00';
    else if (d.endTime && d.startTime && d.endTime <= d.startTime.padStart(5, '0')) e.endTime = 'Slutet måste vara efter starten';
    if (!e.meetingTime && !e.startTime && d.meetingTime.padStart(5, '0') > d.startTime.padStart(5, '0')) e.meetingTime = 'Samlingen bör vara före jaktstart';
  }
  if (step === 'plats') {
    if (!d.meetingPlace.trim() && d.meetingLat == null) e.meetingPlace = 'Välj samlingsplats på kartan eller skriv var ni ses';
  }
  if (step === 'vilka') {
    if (d.memberIds.filter(id => id !== meId).length + d.guests.length < 1) e.recipients = 'Bjud in minst en person';
  }
  return e;
}

export function validateAll(d: PlanDraft, meId?: string | null): { step: StepKey; errors: Errors } | null {
  for (const s of STEPS) {
    const errors = validateStep(s.key, d, meId);
    if (Object.keys(errors).length) return { step: s.key, errors };
  }
  return null;
}

// ─── Mottagare ────────────────────────────────────────────────────

/** Tolkar "namn@exempel.se", "070-123 45 67" eller "Johan Berg johan@x.se". */
export function parseGuest(contact: string, name: string): Guest | { error: string } {
  const c = contact.trim();
  const email = c.match(/[^\s<>]+@[^\s<>]+\.[^\s<>]+/)?.[0]?.toLowerCase();
  const digits = c.replace(/[^\d+]/g, '');
  if (email) return { key: `g:${email}`, name: name.trim() || email, email };
  if (digits.replace(/\D/g, '').length >= 7) return { key: `g:${digits}`, name: name.trim() || c, phone: c };
  return { error: 'Skriv en e-postadress eller ett telefonnummer' };
}

// ─── Pass ─────────────────────────────────────────────────────────

/** Låser eller låser upp ett pass för lottningen. */
export function setPostLocked(d: PlanDraft, markerId: string, locked: boolean): PlanDraft {
  return { ...d, satar: d.satar.map(s => ({ ...s, markers: s.markers.map(m => m.id === markerId ? { ...m, locked: locked || undefined } : m) })) };
}

export function assignPost(d: PlanDraft, markerId: string, key: string | null, members: PlanMember[]): PlanDraft {
  const r = key ? recipientsOf(d, members).find(x => x.key === key) : undefined;
  return {
    ...d,
    satar: d.satar.map(s => ({
      ...s,
      markers: s.markers.map(m => {
        // En person kan bara ha ett pass: flytta i stället för att dubblera.
        if (m.id !== markerId) return key && m.assigneeKey === key ? { ...m, assigneeKey: null, assignedName: undefined } : m;
        return { ...m, assigneeKey: r?.key ?? null, assignedName: r?.name };
      }),
    })),
  };
}

// ─── Till och från API:t ──────────────────────────────────────────

export function toPlanHuntInput(d: PlanDraft, members: PlanMember[]): PlanHuntInput {
  const recipients = recipientsOf(d, members).map(r => ({
    memberId: r.memberId ?? null,
    email: r.memberId ? null : r.email ?? null,
    phone: r.memberId ? null : r.phone ?? null,
    name: r.name,
    passLabel: passLabelFor(d, r.key),
  }));
  return {
    title: d.title.trim(),
    startTime: toIso(d.date, d.startTime)!,
    endTime: d.endTime ? toIso(d.date, d.endTime) : null,
    meetingTime: toIso(d.date, d.meetingTime)!,
    meetingPlace: d.meetingPlace.trim() || null,
    meetingLat: d.meetingLat,
    meetingLng: d.meetingLng,
    message: d.message.trim() || null,
    contactName: d.contactName.trim() || null,
    contactPhone: d.contactPhone.trim() || null,
    satPlanJson: JSON.stringify({ version: 2, satar: d.satar, utrustning: d.utrustning }),
    recipients,
  };
}

/** Bygger ett utkast av en sparad jakt (för "Ändra"). Äldre planer (utan version) läses så gott det går. */
export function draftFromEvent(ev: MarkCalendarEvent, members: PlanMember[]): PlanDraft {
  const start = new Date(ev.startTime);
  const plan = (() => { try { return ev.satPlanJson ? JSON.parse(ev.satPlanJson) : null; } catch { return null; } })();
  const satar = parsePlanSatar(plan);
  const { memberIds, guests } = peopleFromInvites(ev.invites as CalendarEventInvite[], members);
  return {
    version: 2,
    title: ev.title,
    date: isoDate(start),
    meetingTime: ev.meetingTime ? hhmm(new Date(ev.meetingTime)) : hhmm(start),
    startTime: hhmm(start),
    endTime: ev.endTime ? hhmm(new Date(ev.endTime)) : '',
    meetingPlace: ev.meetingPlace || ev.location || '',
    meetingLat: ev.meetingLat ?? null,
    meetingLng: ev.meetingLng ?? null,
    satar,
    memberIds,
    guests,
    message: ev.message || plan?.anteckningar || '',
    utrustning: Array.isArray(plan?.utrustning) ? plan.utrustning : [],
    contactName: ev.contactName || '',
    contactPhone: ev.contactPhone || '',
    sendEmail: true,
  };
}

/** En mall (ny eller gammal SatPlanerare-mall) läggs över utkastet: såtar, utrustning och meddelande. */
export function applyTemplate(d: PlanDraft, planJson: string): PlanDraft {
  const p = JSON.parse(planJson);
  return {
    ...d,
    satar: templateSatar(planJson) ?? d.satar,
    utrustning: Array.isArray(p.utrustning) ? p.utrustning : d.utrustning,
    message: p.message || p.anteckningar || d.message,
  };
}

export function templateJson(d: PlanDraft): string {
  return JSON.stringify({ version: 2, satar: d.satar.map(s => ({ ...s, markers: s.markers.map(m => ({ ...m, assigneeKey: null, assignedName: undefined, locked: undefined })) })), utrustning: d.utrustning, message: d.message });
}

// ─── Utkast i localStorage ────────────────────────────────────────

const draftKey = (markId: string) => `qvenari_planera_${markId}`;

export function loadDraft(markId: string): PlanDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(markId));
    const d = raw ? JSON.parse(raw) : null;
    return d?.version === 2 ? d : null;
  } catch { return null; }
}

export function saveDraft(markId: string, d: PlanDraft): void {
  try { localStorage.setItem(draftKey(markId), JSON.stringify(d)); } catch { /* privat läge */ }
}

export function clearDraft(markId: string): void {
  try { localStorage.removeItem(draftKey(markId)); } catch { /* */ }
}
